/**
 * AssetLibraryManager — the full asset-management UI for one
 * contributor-owned arche.
 *
 * Three sections:
 *   1. Header: enable/disable toggle, suspended banner,
 *      quota meter, billing summary.
 *   2. Uploader: drag-drop / file picker, multi-file with progress.
 *   3. Asset grid: paginated cards with thumbnail (from contributor
 *      preview endpoint for unpublished, signed URL for published),
 *      tags, status, kebab menu (rename / edit tags / delete).
 *
 * Read-only for unauthorized states: when the library is disabled
 * or suspended, the list still loads but uploads are blocked.
 */

import * as React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { api, type ContributorArcheAsset } from '@/lib/api';
import {
  useArcheAssetLibraryConfig,
  useArcheAssetBillingSummary,
  useArcheAssetsList,
  useEnableArcheAssetLibrary,
  useDisableArcheAssetLibrary,
  useUploadArcheAsset,
  useSoftDeleteArcheAsset,
} from '../hooks/useArcheAssetLibrary';

interface AssetLibraryManagerProps {
  archeId: string;
}

function formatBytes(b: number): string {
  if (b >= 1024 ** 3) return `${(b / 1024 ** 3).toFixed(2)} GB`;
  if (b >= 1024 ** 2) return `${(b / 1024 ** 2).toFixed(2)} MB`;
  if (b >= 1024) return `${(b / 1024).toFixed(2)} KB`;
  return `${b} B`;
}

const ACCEPTED_MIMES = 'image/png,image/jpeg,image/webp';
const MAX_UPLOAD_BYTES = 50 * 1024 * 1024;

export function AssetLibraryManager({ archeId }: AssetLibraryManagerProps) {
  const configQuery = useArcheAssetLibraryConfig(archeId);
  const billingQuery = useArcheAssetBillingSummary(archeId);
  const enableMutation = useEnableArcheAssetLibrary(archeId);
  const disableMutation = useDisableArcheAssetLibrary(archeId);

  if (configQuery.isLoading) {
    return <div className="text-muted-foreground">Loading…</div>;
  }
  if (configQuery.error || !configQuery.data) {
    return <div className="text-destructive">Failed to load asset library config.</div>;
  }

  const config = configQuery.data.config;
  const isSuspended = !!config.suspendedAt;
  const isEnabled = config.enabled;
  const usagePct =
    config.effectiveCaps.maxStorageBytes > 0
      ? (config.usage.activeStorageBytes / config.effectiveCaps.maxStorageBytes) * 100
      : 0;

  return (
    <div className="space-y-6">
      {isSuspended && (
        <Card className="border-destructive bg-destructive/5">
          <CardContent className="pt-6">
            <div className="font-semibold text-destructive">Asset library suspended</div>
            <div className="mt-1 text-sm text-muted-foreground">
              Your asset library is suspended due to insufficient credits ({config.suspendReason}).
              Top up your balance to resume uploads — already-published manifests continue to serve
              to your end users.
            </div>
            <Button variant="outline" className="mt-3" asChild>
              <a href="/credits">Top up credits</a>
            </Button>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Asset Library</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div>
              <div className="font-medium">{isEnabled ? 'Enabled' : 'Disabled'}</div>
              <div className="text-sm text-muted-foreground">
                {isEnabled
                  ? 'Contributors can upload and edit. Runtime byte-serve continues.'
                  : 'Uploads are blocked. Already-published manifests continue to serve.'}
              </div>
            </div>
            <Switch
              checked={isEnabled}
              onCheckedChange={(next) => {
                if (next) enableMutation.mutate();
                else disableMutation.mutate();
              }}
              disabled={enableMutation.isPending || disableMutation.isPending || isSuspended}
              aria-label="Toggle asset library enabled state"
            />
          </div>

          <div className="mt-6 grid grid-cols-2 gap-4">
            <div>
              <div className="text-sm font-medium">Storage</div>
              <div className="text-sm text-muted-foreground">
                {formatBytes(config.usage.activeStorageBytes)} /{' '}
                {formatBytes(config.effectiveCaps.maxStorageBytes)} ({usagePct.toFixed(1)}%)
              </div>
              <div className="mt-1 h-2 w-full overflow-hidden rounded bg-muted">
                <div
                  className="h-full bg-primary transition-all"
                  style={{ width: `${Math.min(100, usagePct)}%` }}
                />
              </div>
            </div>
            <div>
              <div className="text-sm font-medium">Assets</div>
              <div className="text-sm text-muted-foreground">
                {config.usage.activeAssetCount.toLocaleString()} /{' '}
                {config.effectiveCaps.maxAssets.toLocaleString()}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <BillingSummarySection
        loading={billingQuery.isLoading}
        summary={billingQuery.data?.summary}
      />

      {isEnabled && !isSuspended && <UploadDropzone archeId={archeId} />}

      <AssetGrid archeId={archeId} />
    </div>
  );
}

function BillingSummarySection({
  loading,
  summary,
}: {
  loading: boolean;
  summary?: {
    yesterday: {
      storageChargeCredits: number;
      egressChargeCredits: number;
      chargeStatus: string;
    } | null;
    monthToDate: {
      storageChargeCredits: number;
      egressChargeCredits: number;
      daysCharged: number;
      daysFailed: number;
    };
  };
}) {
  if (loading || !summary) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Billing</CardTitle>
        </CardHeader>
        <CardContent className="text-muted-foreground">Loading…</CardContent>
      </Card>
    );
  }
  const mtdTotal =
    summary.monthToDate.storageChargeCredits + summary.monthToDate.egressChargeCredits;
  return (
    <Card>
      <CardHeader>
        <CardTitle>Billing</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <div className="text-sm font-medium">Yesterday</div>
          {summary.yesterday ? (
            <div className="text-sm text-muted-foreground">
              Storage: {summary.yesterday.storageChargeCredits.toFixed(4)} credits · Egress:{' '}
              {summary.yesterday.egressChargeCredits.toFixed(4)} credits ·{' '}
              <span className="font-mono">{summary.yesterday.chargeStatus}</span>
            </div>
          ) : (
            <div className="text-sm text-muted-foreground">No snapshot yet.</div>
          )}
        </div>
        <div>
          <div className="text-sm font-medium">Month to date</div>
          <div className="text-sm text-muted-foreground">
            {mtdTotal.toFixed(4)} credits total · {summary.monthToDate.daysCharged} days charged ·{' '}
            {summary.monthToDate.daysFailed} failed
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function UploadDropzone({ archeId }: { archeId: string }) {
  const uploadMutation = useUploadArcheAsset(archeId);
  const [tagInput, setTagInput] = React.useState('');
  const [progress, setProgress] = React.useState<
    Array<{ name: string; status: 'uploading' | 'done' | 'error'; error?: string }>
  >([]);
  const inputRef = React.useRef<HTMLInputElement>(null);

  async function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return;
    const tags = tagInput
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);

    const next: typeof progress = [];
    for (const f of Array.from(files)) {
      if (f.size > MAX_UPLOAD_BYTES) {
        next.push({ name: f.name, status: 'error', error: 'exceeds 50 MB cap' });
        continue;
      }
      next.push({ name: f.name, status: 'uploading' });
    }
    setProgress(next);

    // Sequential to keep the UI simple. Power users use the CLI.
    for (let i = 0; i < next.length; i++) {
      if (next[i].status !== 'uploading') continue;
      const file = files.item(i);
      if (!file) continue;
      try {
        await uploadMutation.mutateAsync({ file, tags: tags.length > 0 ? tags : undefined });
        setProgress((p) =>
          p.map((row, idx) => (idx === i ? { ...row, status: 'done' as const } : row)),
        );
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        setProgress((p) =>
          p.map((row, idx) => (idx === i ? { ...row, status: 'error' as const, error: msg } : row)),
        );
      }
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Upload</CardTitle>
      </CardHeader>
      <CardContent>
        <label className="block text-sm font-medium" htmlFor="asset-tag-input">
          Tags (comma-separated, applied to the next batch)
        </label>
        <input
          id="asset-tag-input"
          type="text"
          className="mt-1 w-full rounded border border-input bg-background p-2 text-sm"
          placeholder="pack1, premium"
          value={tagInput}
          onChange={(e) => setTagInput(e.target.value)}
        />

        <div
          role="button"
          tabIndex={0}
          onClick={() => inputRef.current?.click()}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click();
          }}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            handleFiles(e.dataTransfer.files);
          }}
          className="mt-3 cursor-pointer rounded-lg border-2 border-dashed border-input p-8 text-center hover:bg-muted/40"
          aria-label="Upload images"
        >
          <div className="text-sm font-medium">Drop images here or click to browse</div>
          <div className="text-xs text-muted-foreground">PNG, JPEG, WebP · max 50 MB each</div>
        </div>
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPTED_MIMES}
          multiple
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
        />

        {progress.length > 0 && (
          <ul className="mt-4 space-y-1 text-sm">
            {progress.map((row, i) => (
              <li key={i} className="flex items-center justify-between">
                <span className="truncate">{row.name}</span>
                <span
                  className={
                    row.status === 'done'
                      ? 'text-green-600'
                      : row.status === 'error'
                        ? 'text-destructive'
                        : 'text-muted-foreground'
                  }
                >
                  {row.status === 'done'
                    ? 'finalized'
                    : row.status === 'error'
                      ? `failed: ${row.error}`
                      : 'uploading…'}
                </span>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function AssetGrid({ archeId }: { archeId: string }) {
  const [statusFilter, setStatusFilter] = React.useState<string>('active');
  const listQuery = useArcheAssetsList(archeId, { status: statusFilter });
  const deleteMutation = useSoftDeleteArcheAsset(archeId);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>Assets</span>
          <select
            className="rounded border border-input bg-background p-1 text-sm font-normal"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            aria-label="Filter by status"
          >
            <option value="active">Active</option>
            <option value="pending_validation">Pending</option>
            <option value="validation_failed">Failed</option>
            <option value="soft_deleted">Deleted</option>
            <option value="all_visible">All visible</option>
          </select>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {listQuery.isLoading ? (
          <div className="text-muted-foreground">Loading…</div>
        ) : !listQuery.data || listQuery.data.assets.length === 0 ? (
          <div className="text-muted-foreground">No assets match.</div>
        ) : (
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
            {listQuery.data.assets.map((asset) => (
              <AssetCard
                key={asset.assetId}
                archeId={archeId}
                asset={asset}
                onDelete={() => deleteMutation.mutate(asset.assetId)}
                deleting={deleteMutation.isPending}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function AssetCard({
  archeId,
  asset,
  onDelete,
  deleting,
}: {
  archeId: string;
  asset: ContributorArcheAsset;
  onDelete: () => void;
  deleting: boolean;
}) {
  // Only `active` assets get a preview byte-stream. Other statuses
  // render a placeholder.
  const previewSrc =
    asset.status === 'active' ? api.buildArcheAssetPreviewUrl(archeId, asset.assetId) : null;

  return (
    <div className="overflow-hidden rounded border">
      <div className="aspect-square bg-muted flex items-center justify-center">
        {previewSrc ? (
          <img
            src={previewSrc}
            alt={asset.altText ?? asset.name ?? asset.assetId}
            className="h-full w-full object-cover"
            loading="lazy"
          />
        ) : (
          <span className="text-xs text-muted-foreground">{asset.status}</span>
        )}
      </div>
      <div className="p-3">
        <div className="truncate text-sm font-medium" title={asset.name ?? asset.assetId}>
          {asset.name ?? asset.assetId}
        </div>
        <div className="mt-1 text-xs text-muted-foreground">
          {asset.widthPx}×{asset.heightPx} · {formatBytes(asset.sizeBytes)}
        </div>
        {asset.tags.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {asset.tags.map((t) => (
              <Badge key={t} variant="secondary">
                {t}
              </Badge>
            ))}
          </div>
        )}
        {asset.status === 'active' && (
          <Button
            variant="outline"
            size="sm"
            className="mt-3 w-full"
            onClick={() => {
              if (confirm('Soft-delete this asset? Already-published manifests are unaffected.')) {
                onDelete();
              }
            }}
            disabled={deleting}
          >
            Delete
          </Button>
        )}
      </div>
    </div>
  );
}
